package com.trevorwiebe.timesheet.loads.domain

import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.drops
import com.trevorwiebe.timesheet.loads.domain.model.hasCarrier
import com.trevorwiebe.timesheet.loads.domain.model.pickups
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.TripTypeData
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.plus

sealed interface TruckMatch {
    data class Manual(val equipmentId: String) : TruckMatch
    data class Matched(val equipmentId: String) : TruckMatch
    data class Ambiguous(val equipmentIds: Set<String>) : TruckMatch
    data object None : TruckMatch
}

val TruckMatch.resolvedTruckId: String?
    get() = when (this) {
        is TruckMatch.Manual -> equipmentId
        is TruckMatch.Matched -> equipmentId
        else -> null
    }

/**
 * Works out which truck hauled [load]. A hand-set [Load.truckId] always wins; otherwise the
 * driver's mileage log is searched for an entry on the load's scheduled day (or the day after,
 * for deliveries that run past midnight) at one of the load's stops.
 *
 * The entry's trip type is deliberately ignored — drivers forget to flip Loading/Unloading —
 * but it must name a location, so free-text "Other" entries never match. Drop stops are
 * checked first; pickups are only a fallback.
 *
 * [entries] should cover the load's scheduled day and the following day.
 */
fun resolveTruck(load: Load, entries: List<MileageEntry>): TruckMatch {
    if (load.truckId.isNotEmpty()) return TruckMatch.Manual(load.truckId)
    val date = load.scheduledDate ?: return TruckMatch.None
    if (load.hasCarrier || load.employeeId.isEmpty()) return TruckMatch.None

    val window = date..date.plus(1, DateTimeUnit.DAY)
    val candidates = entries.filter { it.employeeId == load.employeeId && it.date.date in window }

    fun trucksAt(locationIds: Set<String>): Set<String> = candidates
        .filter { it.stopLocationId in locationIds }
        .mapTo(mutableSetOf()) { it.equipmentId }

    val trucks = trucksAt(load.drops.mapTo(mutableSetOf()) { it.locationId })
        .ifEmpty { trucksAt(load.pickups.mapTo(mutableSetOf()) { it.locationId }) }

    return when (trucks.size) {
        0 -> TruckMatch.None
        1 -> TruckMatch.Matched(trucks.single())
        else -> TruckMatch.Ambiguous(trucks)
    }
}

private val MileageEntry.stopLocationId: String?
    get() = when (val data = tripTypeData) {
        is TripTypeData.Loading -> data.locationId
        is TripTypeData.Unloading -> data.locationId
        is TripTypeData.Other -> data.locationId
        else -> null
    }?.takeIf { it.isNotBlank() }
